import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, RefreshCw, Save, UploadCloud } from 'lucide-react';
import {
  Emitente,
  auditarNumeracaoEmitente,
  createEmitente,
  getEmitente,
  resyncNumeracaoEmitente,
  updateEmitente
} from '../api/emitente';
import { getCertificado } from '../api/certificado';
import { apiError } from '../api/client';
import FormField from '../components/FormField';
import LoadingSpinner from '../components/LoadingSpinner';
import CertificadoCard from '../components/CertificadoCard';
import CertificadoUploadModal from '../components/CertificadoUploadModal';
import CepLookupField from '../components/CepLookupField';
import CnpjLookupField from '../components/CnpjLookupField';

const schema = z.object({
  // NT 2026.004: CNPJ pode ser alfanumerico (12 alfanumericos + 2 digitos do DV).
  // Preserva letras + uppercase; rejeita entrada com tamanho != 14.
  cnpj: z
    .string()
    .transform((v) => v.toUpperCase().trim())
    .refine((v) => /^[A-Z0-9]{14}$/.test(v), 'CNPJ inválido (use 12 alfanuméricos + 2 dígitos do DV)'),
  razao_social: z.string().min(2, 'Informe a raz?o social'),
  nome_fantasia: z.string().optional().or(z.literal('')),
  ie: z.string().optional().or(z.literal('')),
  im: z.string().optional().or(z.literal('')),
  cnae: z.string().optional().or(z.literal('')),
  crt: z.string().optional().or(z.literal('')),
  cep: z.string().optional().or(z.literal('')),
  logradouro: z.string().optional().or(z.literal('')),
  numero: z.string().optional().or(z.literal('')),
  bairro: z.string().optional().or(z.literal('')),
  complemento: z.string().optional().or(z.literal('')),
  cmun: z.string().optional().or(z.literal('')),
  xmun: z.string().optional().or(z.literal('')),
  uf: z.string().optional().or(z.literal('')),
  email: z.string().email('E-mail inválido').optional().or(z.literal('')),
  email_envio_notas: z.string().email('E-mail inválido').optional().or(z.literal('')),
  envio_email_automatico: z.boolean().default(true),
  telefone: z.string().optional().or(z.literal('')),
  ambiente: z.enum(['homologacao', 'producao']).default('producao'),
  emite_nfe: z.boolean().default(true),
  emite_cte: z.boolean().default(true),
  emite_ctos: z.boolean().default(false),
  emite_mdfe: z.boolean().default(false),
  serie_nfe: z.coerce.number().int().min(1).optional(),
  serie_cte: z.coerce.number().int().min(1).optional(),
  serie_ctos: z.coerce.number().int().min(1).optional(),
  serie_mdfe: z.coerce.number().int().min(1).optional(),
  prox_numero_nfe: z.coerce.number().int().min(1).optional(),
  prox_numero_cte: z.coerce.number().int().min(1).optional(),
  prox_numero_ctos: z.coerce.number().int().min(1).optional(),
  prox_numero_mdfe: z.coerce.number().int().min(1).optional()
});

type FormValues = z.infer<typeof schema>;

export default function EmitenteEditPage() {
  const { id } = useParams();
  const isNew = !id || id === 'novo';
  const emitenteId = isNew ? null : Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [syncingNumeracao, setSyncingNumeracao] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [numeracaoMsg, setNumeracaoMsg] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);

  const emitenteQ = useQuery({
    enabled: !isNew && !!emitenteId,
    queryKey: ['emitente', emitenteId],
    queryFn: () => getEmitente(emitenteId!)
  });

  const certQ = useQuery({
    enabled: !isNew && !!emitenteId,
    queryKey: ['emitente-cert', emitenteId],
    queryFn: () => getCertificado(emitenteId!)
  });

  const numeracaoQ = useQuery({
    enabled: !isNew && !!emitenteId,
    queryKey: ['emitente-numeracao', emitenteId],
    queryFn: () => auditarNumeracaoEmitente(emitenteId!),
    refetchOnWindowFocus: false
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      ambiente: 'producao',
      envio_email_automatico: true,
      emite_nfe: true,
      emite_cte: true,
      emite_ctos: false,
      emite_mdfe: false
    } as FormValues
  });

  const cnpjVal = watch('cnpj') || '';
  const cepVal = watch('cep') || '';

  useEffect(() => {
    if (emitenteQ.data) {
      // API devolve null em campos vazios e crt numérico; o schema espera
      // string/'' — sem sanitizar, a validação trava o salvar silenciosamente.
      const limpo: Record<string, unknown> = { ...(emitenteQ.data as unknown as Record<string, unknown>) };
      for (const [k, v] of Object.entries(limpo)) {
        if (v === null) limpo[k] = '';
      }
      if (emitenteQ.data.crt != null) limpo.crt = String(emitenteQ.data.crt);
      reset(limpo as unknown as FormValues);
    }
  }, [emitenteQ.data, reset]);

  function onInvalid(errs: Record<string, unknown>) {
    const campos = Object.keys(errs).join(', ');
    setErr(`Não foi possível salvar — verifique os campos: ${campos}`);
  }

  async function onSubmit(values: FormValues) {
    setSaving(true);
    setErr(null);
    try {
      let saved: Emitente;
      if (isNew) {
        saved = await createEmitente(values);
      } else {
        saved = await updateEmitente(emitenteId!, values);
      }
      qc.invalidateQueries({ queryKey: ['emitentes'] });
      qc.invalidateQueries({ queryKey: ['emitente', saved.id] });
      navigate(`/emitentes/${saved.id}`, { replace: true });
    } catch (e) {
      setErr(apiError(e, 'Erro ao salvar emitente'));
    } finally {
      setSaving(false);
    }
  }

  async function sincronizarNumeracao() {
    if (!emitenteId) return;
    setSyncingNumeracao(true);
    setNumeracaoMsg(null);
    setErr(null);
    try {
      const result = await resyncNumeracaoEmitente(emitenteId);
      const total = Object.keys(result.mudancas || {}).length;
      setNumeracaoMsg(total ? `${total} contador(es) ajustado(s).` : 'Numeração já estava coerente.');
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['emitente', emitenteId] }),
        qc.invalidateQueries({ queryKey: ['emitente-numeracao', emitenteId] }),
        qc.invalidateQueries({ queryKey: ['emitentes'] })
      ]);
      const atualizado = await getEmitente(emitenteId);
      reset(atualizado as unknown as FormValues);
    } catch (e) {
      setErr(apiError(e, 'Erro ao sincronizar numeração'));
    } finally {
      setSyncingNumeracao(false);
    }
  }

  if (!isNew && emitenteQ.isLoading) {
    return (
      <div className="py-10 grid place-items-center">
        <LoadingSpinner label="Carregando emitente..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/70 dark:shadow-none md:p-5">
        <div className="flex items-center gap-2">
        <Link to="/emitentes" className="btn-ghost h-11 w-11 !px-2 !py-1 md:h-9 md:w-9">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <div className="eyebrow">Empresa emitente</div>
          <h1 className="h-display mt-1 text-2xl text-slate-950 md:text-3xl dark:text-slate-100">
            {isNew ? 'Novo emitente' : 'Editar emitente'}
          </h1>
          {!isNew && emitenteQ.data && (
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 truncate">{emitenteQ.data.razao_social}</p>
          )}
        </div>
        </div>
      </header>

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6">
        {/* Dados principais */}
        <section className="card-surface p-5 space-y-4">
          <div>
            <h2 className="eyebrow">Dados da empresa</h2>
            <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
              Identificação fiscal do CNPJ usado na emissão.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CnpjLookupField
              value={cnpjVal}
              onChange={(v) => setValue('cnpj', v)}
              onAutofill={(data) => {
                if (data.razao_social) setValue('razao_social', data.razao_social);
                if (data.nome_fantasia) setValue('nome_fantasia', data.nome_fantasia);
                if (data.cep) setValue('cep', data.cep);
                if (data.logradouro) setValue('logradouro', data.logradouro);
                if (data.numero) setValue('numero', data.numero);
                if (data.bairro) setValue('bairro', data.bairro);
                if (data.complemento) setValue('complemento', data.complemento);
                if (data.uf) setValue('uf', data.uf);
                if (data.municipio) setValue('xmun', data.municipio);
                if (data.codigo_municipio_ibge) setValue('cmun', String(data.codigo_municipio_ibge));
                if (data.email) setValue('email', data.email);
                if (data.ddd_telefone_1) setValue('telefone', data.ddd_telefone_1);
                if (data.cnae_fiscal) setValue('cnae', String(data.cnae_fiscal));
              }}
              disabled={!isNew}
              hint={!isNew ? 'CNPJ não pode ser alterado após o cadastro.' : undefined}
            />
            <FormField
              label="Razão social *"
              className="md:col-span-2"
              {...register('razao_social')}
              error={errors.razao_social?.message}
            />
            <FormField label="Nome fantasia" {...register('nome_fantasia')} className="md:col-span-2" />
            <FormField label="IE" {...register('ie')} />
            <FormField label="Inscrição Municipal" {...register('im')} />
            <FormField label="CNAE" {...register('cnae')} />
            <FormField label="CRT" {...register('crt')} hint="1,2 ou 3" />
            <FormField label="E-mail" {...register('email')} error={errors.email?.message} />
            <FormField label="Telefone" {...register('telefone')} />
            <div>
              <label className="field-label">Ambiente</label>
              <select className="field-input" {...register('ambiente')}>
                <option value="homologacao">Homologação</option>
                <option value="producao">Produção</option>
              </select>
            </div>
          </div>
        </section>

        {/* Endereço */}
        <section className="card-surface p-5 space-y-4">
          <div>
            <h2 className="eyebrow">Endereço</h2>
            <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
              Endereço fiscal do emitente conforme cadastro municipal/SEFAZ.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <CepLookupField
              value={cepVal}
              onChange={(v) => setValue('cep', v)}
              onAutofill={(data) => {
                if (data.logradouro) setValue('logradouro', data.logradouro);
                if (data.bairro) setValue('bairro', data.bairro);
                if (data.localidade) setValue('xmun', data.localidade);
                if (data.uf) setValue('uf', data.uf);
                if (data.ibge) setValue('cmun', data.ibge);
              }}
            />
            <FormField label="Logradouro" className="md:col-span-3" {...register('logradouro')} />
            <FormField label="Número" {...register('numero')} />
            <FormField label="Bairro" {...register('bairro')} className="md:col-span-2" />
            <FormField label="Complemento" {...register('complemento')} />
            <FormField label="Código Município (IBGE)" {...register('cmun')} />
            <FormField label="Município" className="md:col-span-2" {...register('xmun')} />
            <FormField label="UF" {...register('uf')} maxLength={2} />
          </div>
        </section>

        {/* Envio automático de notas por e-mail */}
        <section className="card-surface p-5 space-y-4">
          <div>
            <h2 className="eyebrow">Envio automático por e-mail</h2>
            <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
              Quando ativado, após a SEFAZ autorizar um documento o sistema envia
              automaticamente um e-mail com o <b>XML e o PDF</b> em anexo para o e-mail do
              cadastro do destinatário/tomador e para o e-mail abaixo (se preenchido).
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="flex items-start gap-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition hover:border-emerald-500/40 dark:border-white/10 dark:bg-white/[0.02] dark:shadow-none">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-emerald-500"
                {...register('envio_email_automatico')}
              />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm">Enviar automaticamente</div>
                <div className="text-xs text-neutral-500 dark:text-neutral-400">
                  Dispara após cada autorização
                </div>
              </div>
            </label>
            <FormField
              label="E-mail para envio das notas"
              className="md:col-span-2"
              {...register('email_envio_notas')}
              error={errors.email_envio_notas?.message}
              hint="Recebe cópia de toda nota autorizada. Em homologação, só este e-mail recebe."
            />
          </div>
        </section>

        {/* Tipos de documentos fiscais que o emitente vai emitir */}
        <section className="card-surface p-5 space-y-4">
          <div>
            <h2 className="eyebrow">Documentos fiscais habilitados</h2>
            <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-1">
              Marque apenas os tipos de documento que <b>este emitente realmente emite</b>. As
              abas e os atalhos de "Emitir" no painel da empresa só vão exibir o que estiver
              marcado aqui. Emitentes que só fazem CT-e não veem mais NF-e por engano.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { name: 'emite_nfe' as const, label: 'NF-e', hint: 'Modelo 55, venda de mercadoria' },
              { name: 'emite_cte' as const, label: 'CT-e', hint: 'Modelo 57, transporte de carga' },
              { name: 'emite_ctos' as const, label: 'CT-e OS', hint: 'Modelo 67, passageiros, pedágio' },
              { name: 'emite_mdfe' as const, label: 'MDF-e', hint: 'Modelo 58, manifesto de carga' }
            ].map((opt) => (
              <label
                key={opt.name}
                className="flex min-h-[96px] items-start gap-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition hover:border-emerald-500/40 dark:border-white/10 dark:bg-white/[0.02] dark:shadow-none"
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 accent-emerald-500"
                  {...register(opt.name)}
                />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm">{opt.label}</div>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400">{opt.hint}</div>
                </div>
              </label>
            ))}
          </div>
        </section>

        {/* Séries e numeração de documentos fiscais */}
        <section className="card-surface p-5 space-y-4">
          <div>
            <h2 className="eyebrow">Séries e numeração</h2>
            <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-1">
              Configure a série e o <b>próximo número</b> de cada tipo de documento. Use isto para
              alinhar a numeração com o último documento emitido em sistema anterior. Ex.: se a
              última CT-e autorizada foi a nº 5, defina <i>Próximo nº CT-e</i> como <b>6</b>.
              Se houver documentos antigos importados com numeração maior, o sistema ainda assim
              respeitará o valor configurado aqui.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <FormField
              label="Série NF-e"
              type="number"
              min={1}
              {...register('serie_nfe')}
              error={errors.serie_nfe?.message}
            />
            <FormField
              label="Próximo nº NF-e"
              type="number"
              min={1}
              {...register('prox_numero_nfe')}
              error={errors.prox_numero_nfe?.message}
            />
            <FormField
              label="Série CT-e"
              type="number"
              min={1}
              {...register('serie_cte')}
              error={errors.serie_cte?.message}
            />
            <FormField
              label="Próximo nº CT-e"
              type="number"
              min={1}
              {...register('prox_numero_cte')}
              error={errors.prox_numero_cte?.message}
            />
            <FormField
              label="Série CT-e OS"
              type="number"
              min={1}
              {...register('serie_ctos')}
              error={errors.serie_ctos?.message}
            />
            <FormField
              label="Próximo nº CT-e OS"
              type="number"
              min={1}
              {...register('prox_numero_ctos')}
              error={errors.prox_numero_ctos?.message}
            />
            <FormField
              label="Série MDF-e"
              type="number"
              min={1}
              {...register('serie_mdfe')}
              error={errors.serie_mdfe?.message}
            />
            <FormField
              label="Próximo nº MDF-e"
              type="number"
              min={1}
              {...register('prox_numero_mdfe')}
              error={errors.prox_numero_mdfe?.message}
            />
          </div>
          {!isNew && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3 dark:border-white/10 dark:bg-white/[0.02]">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="text-sm font-semibold">Auditoria de numeração</div>
                  <div className="text-xs text-neutral-500">
                    Usa somente documentos do ambiente atual do emitente para evitar misturar homologação e produção.
                  </div>
                </div>
                <div className="flex gap-2">
                  <button type="button" className="btn-secondary" onClick={() => numeracaoQ.refetch()} disabled={numeracaoQ.isFetching}>
                    <RefreshCw className={`h-4 w-4 ${numeracaoQ.isFetching ? 'animate-spin' : ''}`} />
                    Auditar
                  </button>
                  <button type="button" className="btn-primary" onClick={sincronizarNumeracao} disabled={syncingNumeracao || numeracaoQ.isLoading}>
                    <RefreshCw className={`h-4 w-4 ${syncingNumeracao ? 'animate-spin' : ''}`} />
                    Ajustar atrasados
                  </button>
                </div>
              </div>
              {numeracaoMsg && <div className="text-xs text-emerald-700 dark:text-emerald-300">{numeracaoMsg}</div>}
              {numeracaoQ.isLoading ? (
                <LoadingSpinner />
              ) : numeracaoQ.data ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-slate-100 text-left text-xs uppercase tracking-[0.08em] text-slate-500 dark:bg-slate-900/80">
                      <tr>
                        <th className="py-2 pr-3">Tipo</th>
                        <th className="py-2 pr-3">Série</th>
                        <th className="py-2 pr-3">Atual</th>
                        <th className="py-2 pr-3">Sugerido</th>
                        <th className="py-2 pr-3">Maior usado</th>
                        <th className="py-2 pr-3">Situação</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                      {numeracaoQ.data.itens.map((item) => (
                        <tr key={item.tipo}>
                          <td className="py-2 pr-3 font-medium">{item.label}</td>
                          <td className="py-2 pr-3">{item.serie}</td>
                          <td className="py-2 pr-3">{item.proximo_atual}</td>
                          <td className="py-2 pr-3">{item.proximo_sugerido}</td>
                          <td className="py-2 pr-3">{item.max_consumido || '-'}</td>
                          <td className="py-2 pr-3">
                            <span className={item.situacao === 'atrasado' ? 'badge-danger' : item.situacao === 'adiantado' ? 'badge-warning' : 'badge-success'}>
                              {item.situacao}
                            </span>
                            {item.max_outros_ambientes > item.max_consumido && (
                              <span className="ml-2 text-neutral-500">outro ambiente: {item.max_outros_ambientes}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          )}
        </section>

        {err && (
          <div className="text-sm text-danger bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md px-3 py-2">
            {err}
          </div>
        )}

        <div className="grid gap-2 rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm dark:border-white/10 dark:bg-slate-950/70 dark:shadow-none sm:flex sm:justify-end">
          <Link to="/emitentes" className="btn-secondary justify-center">
            Cancelar
          </Link>
          <button type="submit" disabled={saving} className="btn-primary justify-center">
            <Save className="h-4 w-4" />
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>

      {/* Certificado digital (apenas quando editando) */}
      {!isNew && emitenteId && (
        <section className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="font-semibold">Certificado digital</h2>
            <button onClick={() => setShowUpload(true)} className="btn-primary justify-center">
              <UploadCloud className="h-4 w-4" />
              Enviar novo
            </button>
          </div>
          {certQ.isLoading && <LoadingSpinner />}
          {!certQ.isLoading && certQ.data && <CertificadoCard cert={certQ.data} />}
          {!certQ.isLoading && !certQ.data && (
            <div className="card-surface p-4 text-sm text-neutral-600 dark:text-neutral-300">
              Nenhum certificado cadastrado. Clique em <b>Enviar novo</b> para enviar um arquivo
              .pfx / .p12.
            </div>
          )}
          {showUpload && (
            <CertificadoUploadModal
              emitenteId={emitenteId}
              onClose={() => setShowUpload(false)}
              onSuccess={() => {
                setShowUpload(false);
                qc.invalidateQueries({ queryKey: ['emitente-cert', emitenteId] });
                qc.invalidateQueries({ queryKey: ['certs-vencendo'] });
              }}
            />
          )}
        </section>
      )}
    </div>
  );
}
